<script setup>
import { ref, onMounted, computed } from "vue";
import { useScrollStore } from "@/stores/storeScroll";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { RouterLink } from "vue-router";
import ProfileFilter from "@/components/profile/ProfileFilter.vue";
import ProfileUserData from "@/components/profile/ProfileUserData.vue";
import ProfileUserReceipts from "@/components/profile/ProfileUserReceipts.vue";
import ProfileUserPrizes from "@/components/profile/ProfileUserPrizes.vue";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";

const storeScroll = useScrollStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();

onMounted( async() => {
	await storeRequests.userInfo();
});
</script>
<template>
	<section class="profile">
		<div class="profile__container">
			<div class="profile__section profile__user-data" id="personalData" :style="{ paddingTop: storeScroll.paddingTop }">
				<div class="container">
					<ProfileUserData />
				</div>
			</div>

			<div class="profile__section profile__user-prizes" id="myPrizes">
				<div class="container">
					<ProfileUserPrizes />
				</div>
			</div>

			<div class="profile__section profile__user-games" id="games">
				<div class="container">
					<div class="user-games">
						<h2 class="user-games__title title2">Игры</h2>
						<p class="user-games__subtitle">
							Играйте в игры и участвуйте в розыгрыше билетов <br />
							в Третьяковскую галерею или сертификаты Яндекс Афиша.
						</p>
						<div class="user-games__container">
							<div class="game" :class="{ '--completed': storeRequests.userData.fillword_success }">
								<div class="game__image">
									<img class="full-img" width="194" height="194" src="@/assets/img/games/game_1_1.webp" alt="изображение" />
								</div>
								<div class="game__content">
									<span class="game__label" v-if="storeRequests.userData.fillword_success">
										<svg width="16" height="16"><use href="#check_completed"></use></svg>
										Пройдено
									</span>
									<h3 class="game__name">ФилВорд</h3>
									<p class="game__desc">Найдите слова, спрятанные в сетке букв — все они связаны с искусством, известными картинами, художниками и крупами!</p>
									<RouterLink to="/quiz_fill_words" class="game__btn btn --blue">Играть</RouterLink>
								</div>
							</div>
							<div class="game" :class="{ '--completed': storeRequests.userData.text_quiz_success }">
								<div class="game__image">
									<img class="full-img" width="194" height="194" src="@/assets/img/games/game_2_2.webp" alt="изображение" />
								</div>
								<div class="game__content">
									<span class="game__label" v-if="storeRequests.userData.text_quiz_success">
										<svg width="16" height="16"><use href="#check_completed"></use></svg>
										Пройдено
									</span>
									<h3 class="game__name">Шедевры в эмодзи</h3>
									<p class="game__desc">Эмодзи — современный визуальный язык знакомый каждому. Больше игр — больше шансов выиграть приз.</p>
									<RouterLink to="/quiz_emoji" class="game__btn btn --blue">Играть</RouterLink>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="profile__section profile__user-receipts" id="uploadedReceipts">
				<div class="container">
					<ProfileUserReceipts />
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.profile__user-games {
	background-image: url("@/assets/img/games/user_games_line.svg");
	background-repeat: no-repeat;
	background-position: 100% 60%; /* x y*/
	background-size: contain; /* w h*/
	padding: 8rem 0;
	@media (max-width: vars.$x480) {
		padding: 4.8rem 0;
		background-image: none;
	}
}
.user-games {
	position: relative;
	max-width: 80rem;
	width: 100%;
	display: flex;
	justify-content: center;
	flex-direction: column;
	margin: 0 auto;
	@media (max-width: vars.$m) {
		max-width: 100%;
	}
}
.user-games__title {
	@media (max-width: vars.$x480) {
		margin-bottom: 3rem;
	}
}
.user-games__subtitle {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	text-align: center;
	margin-bottom: 5rem;
	@media (max-width: vars.$xx) {
		display: none;
	}
}
.user-games__container {
	width: 100%;
	display: grid;
	gap: 3.2rem;
	@media (max-width: vars.$x480) {
		gap: 1.6rem;
	}
}
.game {
	position: relative;
	display: grid;
	grid-template-columns: 19.4rem 1fr;
	align-items: center;
	gap: 2.8rem;
	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: var(--border-radius);
	padding: 2.4rem 2rem 2.4rem 2.4rem;
	background-color: #fff;
	@media (max-width: vars.$x480) {
		grid-template-columns: 1fr;
		padding: 2rem 1.2rem 1.6rem 1.6rem;
	}
	&:last-child {
		.game__image {
			padding: 0;
		}
	}
	&.--completed {
		.game__image {
			@media (max-width: vars.$x480) {
				top: 34%;
			}
		}
	}
}
.game__image {
	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: 1.5rem;
	padding: 1.5rem;
	width: 100%;
	
	flex-shrink: 0;
	@media (max-width: vars.$x480) {
		border-radius: 0.7rem;
		padding: 0.7rem;
		position: absolute;
		top: 24%;
		right: 12px;
		width: 9.6rem;
	}
	img {
		aspect-ratio: 1/1;
	}
}
.game__content {
	display: grid;
	gap: 1rem;
}
.game__label {
	width: fit-content;
	display: flex;
	align-items: center;
	gap: 0.8rem;
	background: var(--fill-accent-1-1);
	border-radius: 0.6rem;
	padding: 0.8rem;

	font-weight: 400;
	font-size: 1.6rem;
	line-height: 140%;
	color: #fff;
	@media (max-width: vars.$x480) {
		padding: 0.6rem 0.5rem 0.2rem;
		gap: 0.6rem;
	}
	svg {
		width: 1.6rem;
		height: 1.6rem;
	}
}
.game__name {
	font-weight: 700;
	font-size: 3.2rem;
	line-height: 100%;
	text-transform: uppercase;
	color: #0c4ca3;
	@media (max-width: vars.$x480) {
		font-size: 2rem;
	}
}
.game__desc {
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 140%;

	@media (max-width: vars.$x480) {
		// max-width: 215px;
		width: 65%;
		font-size: 1.4rem;
	}
}
.game__btn {
	width: fit-content;
	font-size: 2rem;
	padding: 1.7rem 2rem;
	@media (max-width: vars.$x480) {
		padding: 1.3rem 2rem;
		margin-top: 10px;
		width: 65%;
	}
}

.profile {
	position: relative;
}
.profile__container {
}
.profile__user-data {
	background-image: url("@/assets/img/profile_bg.webp");
	background-repeat: no-repeat;
	background-position: center; /* x y*/
	background-size: cover; /* w h*/
}
.profile__user-prizes {
}
.profile__user-receipts {
}

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: translateY(10px);
}
</style>
